import { getCurrentMemberId } from '../auth/current-member-id';
import type { LocalDatabase } from '../db/types';
import { getSyncEngine } from '../sync/engine';

import type { MemberRecord } from './types';

type MemberRow = {
  id: string;
  display_name: string;
  avatar_url: string | null;
  role: string;
  created_at: number;
  updated_at: number;
};

async function getDb(): Promise<LocalDatabase> {
  const engine = getSyncEngine();
  let db = engine.getDatabase();
  if (!db) {
    await engine.start();
    db = engine.getDatabase();
  }
  if (!db) {
    throw new Error('Local database not ready');
  }
  return db;
}

function rowToMember(row: MemberRow): MemberRecord {
  return {
    id: row.id,
    displayName: row.display_name,
    avatarUrl: row.avatar_url,
    role: row.role === 'admin' ? 'admin' : 'member',
    createdAt: new Date(row.created_at).toISOString(),
    updatedAt: new Date(row.updated_at).toISOString(),
  };
}

export async function fetchMemberLocalById(memberId: string): Promise<MemberRecord | null> {
  const db = await getDb();
  const row = await db.getFirstAsync<MemberRow>(`SELECT * FROM members WHERE id = ?`, memberId);
  return row ? rowToMember(row) : null;
}

/** Member from local SQLite (for sidebar and local writes when offline). */
export async function fetchMemberLocal(): Promise<MemberRecord | null> {
  const memberId = getCurrentMemberId();
  if (!memberId) {
    return null;
  }
  return fetchMemberLocalById(memberId);
}

/** Member id for local writes (dev impersonation or first synced member). */
export async function getLocalMemberId(): Promise<string> {
  const member = await fetchMemberLocal();
  if (!member?.id) {
    throw new Error('No local member; sync bootstrap required');
  }
  return member.id;
}
